import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createEmbedTokenManager, type EmbedTokenManager } from '@/lib/yom/tokenManager';
import type { TokenBundle } from '@/lib/yom/types';

/**
 * One token manager per page (keyed by `scope`). Every report on the page
 * shares it, so mounting eight reports costs one backend call, and the
 * manager refreshes the bundle before expiry for all of them.
 *
 * The first bundle is fetched through TanStack Query so the page can render
 * skeleton → error-with-retry with the standard DataState rules.
 */
export function useEmbedTokens(scope: string, fetchBundle: () => Promise<TokenBundle>) {
  const [manager, setManager] = useState<EmbedTokenManager | null>(null);

  useEffect(() => {
    const m = createEmbedTokenManager({ fetchBundle });
    setManager(m);
    return () => m.dispose();
    // fetchBundle is defined inline by callers; the scope is what identifies it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const first = useQuery({
    queryKey: ['embed-tokens', scope],
    queryFn: () => manager!.getBundle(),
    enabled: Boolean(manager),
    // The manager owns freshness; never let Query refetch a token on focus.
    staleTime: Infinity,
    gcTime: 0,
    retry: 1,
  });

  return { manager, first };
}
